import { useDebounceFn } from '@dni-react-hooks/use-debounce-fn'
import { useInterval } from '@dni-react-hooks/use-interval'
import { useMergeRefs } from '@dni-react-hooks/use-merge-refs'
import * as stylex from '@stylexjs/stylex'
import { Children, type ForwardedRef, forwardRef, type ReactNode, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useXStyles, type XStyles } from '../useXStyles/useXStyles'
import { CarouselProvider } from './CarouselProvider'
import { NavButtons, type NavButtonsProps } from './NavButtons'
import { PageIndicators, type PageIndicatorsProps } from './PageIndicators'
import { useCarouselKeyboard } from './useCarouselKeyboard'
import { useHover } from './useHover'
import { useMeasurement } from './useMeasurement'
import { type OnPageChange, usePaging } from './usePaging'

const dynamicStyles = stylex.create({
  maxWidth: (maxWidth: number) => ({ maxWidth }),
})

const _baseStyles = stylex.create({
  container: {
    position: 'relative',
    paddingBottom: 8,
  },
  hidden: {
    margin: '-1px',
    padding: 0,
    borderWidth: 0,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    position: 'absolute',
    whiteSpace: 'nowrap',
    height: 1,
    width: 1,
  },
  slider: {
    position: 'relative',
  },
  sliderContainer: {
    'overflow': 'scroll',
    /**
     * TODO: use animation for smooth scrolling and solve loop animation
     */
    'scrollBehavior': 'smooth',
    /* Firefox */
    'scrollbarWidth': 'none',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  },
  sliderList: {
    display: 'flex',
  },
})

/** Carousel 的命令式操作介面 */
export interface SlideHandle {
  /** 前進到下一頁 */
  goForward: () => void
  /** 返回上一頁 */
  goBack: () => void
  /** 跳轉到指定頁面 */
  goToPage: (proposedIndex: number) => void
}

export interface CarouselCallbacks {
  /** 滑動前的回呼 */
  beforeSlide?: () => void
  /** 滑動後的回呼 */
  afterSlide?: () => void
}

export type CarouselProps = CarouselCallbacks & {
  /** 覆蓋內部各區塊子樣式的 xStyles */
  xStyles?: {
    base?: Partial<XStyles<keyof typeof _baseStyles>>
    navButtons?: NavButtonsProps['xStyles']
    pageIndicators?: XStyles<keyof typeof PageIndicators>
  }
  /** 輪播項目 */
  children: ReactNode
  /** 是否啟用自動播放 */
  autoplay?: boolean
  /**
   * 自動播放間隔（毫秒）
   * @default 3000
   */
  autoplayInterval?: number
  /**
   * 是否啟用鍵盤導覽
   * @default true
   */
  keyboard?: boolean
  /** 是否顯示頁面指示點 */
  showDots?: boolean
  /**
   * 是否顯示前後導覽按鈕
   * @default true
   */
  showNavButtons?: boolean
  /**
   * 捲動模式：wrap（循環）、nowrap（不循環）
   * @default 'wrap'
   */
  wrapMode?: 'nowrap' | 'wrap'
  /** 輪播容器最大寬度 */
  maxWidth?: number
  /** 頁面變更時的回呼 */
  onPageChange?: OnPageChange
  /** 點擊指示點時的回呼 */
  onDotClick?: PageIndicatorsProps['onDotClick']
  /**
   * 每頁顯示幾個項目
   * @default 1
   */
  slidesToShow?: number
  /** 內部捲動容器的 ref */
  containerRef?: ForwardedRef<HTMLDivElement>
  /** 最大指示點數量 */
  maxIndicator?: number
}

/**
 * Carousel 是輪播元件，支援自動播放、觸控滑動、鍵盤導覽與頁面指示器。
 * 基於 nuka-carousel 架構，針對 Pets-UI 客製化。
 */
export const Carousel = forwardRef<SlideHandle, CarouselProps>(
  function Carousel(props: CarouselProps, ref) {
    const {
      xStyles,
      afterSlide,
      autoplay,
      autoplayInterval = 3000,
      beforeSlide,
      children,
      keyboard = true,
      showDots,
      showNavButtons = true,
      wrapMode = 'wrap',
      maxWidth,
      onPageChange,
      onDotClick,
      slidesToShow = 1,
      containerRef: containerRefProp,
      maxIndicator,
    } = props

    const baseStyles = useXStyles(_baseStyles, xStyles?.base)
    const carouselRef = useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const mergeContainerRef = useMergeRefs(containerRef, containerRefProp)
    const items = useMemo(() => Children.toArray(children), [children])
    const totalSlides = useMemo(() => items.length / slidesToShow, [items, slidesToShow])
    const [isTouchStart, setStartTouch] = useState(false)

    // -- update page count and scroll offset based on scroll distance
    const { totalPages, scrollOffset } = useMeasurement({
      element: containerRef,
    })

    // -- paging
    const { currentPage, goBack, goForward, goToPage } = usePaging({
      totalPages,
      wrapMode,
      onPageChange,
    })

    // -- handle touch events
    const handleTouchStart = () => {
      setStartTouch(true)
    }
    const handleTouchEnd = () => {
      setStartTouch(false)
    }
    const handleTouchMove = useDebounceFn(() => {
      if (!containerRef.current) return

      const container = containerRef.current
      const scrollLeft = container.scrollLeft
      const containerWidth = container.clientWidth

      // find the closest page index based on the scroll position
      const currentScrollOffset = scrollOffset[currentPage]
      const diff = scrollLeft - (currentScrollOffset ?? 0)

      if (Math.abs(diff) > containerWidth / 2) {
        if (diff > 0) {
          goForward()
        }
        else {
          goBack()
        }
      }
      else {
        goToPage(currentPage)
      }
    }, 100)

    // -- keyboard nav
    useCarouselKeyboard({
      element: carouselRef,
      enabled: keyboard,
      goForward,
      goBack,
    })

    // -- forward events to ref
    useImperativeHandle(ref, () => ({ goForward, goBack, goToPage }), [
      goForward,
      goBack,
      goToPage,
    ])

    // -- autoplay
    const isHovered = useHover({ element: containerRef })
    const autoplayEnabled = autoplay && !(isHovered || isTouchStart)
    useInterval(goForward, autoplayEnabled ? autoplayInterval : null)

    // -- scroll container when page index changes
    useEffect(() => {
      if (containerRef.current) {
        beforeSlide?.()
        containerRef.current.scrollLeft = scrollOffset[currentPage] ?? 0
        if (afterSlide) {
          setTimeout(() => afterSlide(), 0)
        }
      }
    }, [currentPage, scrollOffset, beforeSlide, afterSlide])

    const providerValues = useMemo(() => ({
      ...props,
      totalPages,
      currentPage,
      scrollOffset,
      goBack,
      goForward,
      goToPage,
    }), [currentPage, goBack, goForward, goToPage, props, scrollOffset, totalPages])

    return (
      <CarouselProvider value={providerValues}>
        <div
          {...stylex.props(baseStyles.container, !!maxWidth && dynamicStyles.maxWidth(maxWidth))}
          tabIndex={keyboard ? 0 : undefined}
          ref={carouselRef}
        >
          <div {...stylex.props(baseStyles.slider)}>
            <div
              {...stylex.props(baseStyles.sliderContainer)}
              ref={mergeContainerRef}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchMove={handleTouchMove}
            >
              <div
                {...stylex.props(baseStyles.sliderList)}
                style={{
                  width: `${totalSlides * 100}%`,
                }}
              >
                {items}
              </div>
              {totalSlides > 1 && showNavButtons && <NavButtons xStyles={xStyles?.navButtons} show={isHovered} />}
              {showDots && (
                <PageIndicators
                  xStyles={xStyles?.pageIndicators}
                  maxIndicator={maxIndicator}
                  onDotClick={onDotClick}
                />
              )}
            </div>
          </div>
        </div>
      </CarouselProvider>
    )
  },
)
